import { useEffect, useState, type ReactNode } from 'react';
import { Modal, Pressable, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import type {
  EquipmentDefault,
  Exercise,
  LocationUnavailable,
} from '@heraion/shared';

import { PickerSessionBand } from '../index';

import type { WeightUnit } from './state/loggerDraft';
import type { DraftEntry } from './state/loggerDraftTypes';
import { useTheme } from './theme';
import { makeStyles } from './ExerciseSwapPicker.styles';
import { CurrentExerciseMachineLine } from './_CurrentExerciseMachineLine';
import { SwapPickerCandidateList } from './_SwapPickerCandidateList';
import { useSwapPickerFilters } from './_useSwapPickerFilters';
import { useLocationUnavailable } from './_useLocationUnavailable';
import { useEquipmentDefault } from './_useEquipmentDefault';
import { UnavailableHereDialog } from './_UnavailableHereDialog';
import { getInstanceSurveyForSlug } from './useEntryHistory';

// Twin of the legacy web's ExerciseSwapPicker (removed with RW-14). Ranking
// (rankSwapCandidates) lives in @heraion/shared so suggestions are
// identical across surfaces.
//
// ⭐ DS-42 (2026-08-15) — THREE BANDS, each one saying its own scope.
// This screen does three different jobs, and until now all three wore the same
// idiom ("text + ▾") on three consecutive rows, told apart only by a border
// that DS-37 measures at 1.1:1. They are not variants of one gesture: they
// have three scopes and three different effects on sets already logged.
//
//   1. Session   — the gym. ⚠️ Writes where the WHOLE session is, and used to
//                  sit in the title row, the least consequential-looking spot
//                  on screen. Now <PickerSessionBand>, with the scope named.
//   2. This exercise — the machine/variant. Keeps every logged set and
//                  re-stamps the tag on it (§8.4). States that consequence.
//   3. Another exercise — the list. Keeps the sets on a same-kind swap and
//                  CLEARS them across kinds, with a confirm
//                  (the cross-kind confirm in useReplaceExercise.ts).
//
// Antenore approved this shape on 2026-08-15 and rejected the other reading of
// his brief (picking a variant from the LIST's rows): a per-row control on a
// long list is input fatigue. 📖 equipment-model.md §10.4c.
//
// ⚠️ EQ-1 D2b — the two `location`s on this screen are NOT the same one, and
// the difference is deliberate. The `location` prop ranks the machine trays by
// where THIS entry actually is; the `gymChip` writes where the SESSION is.
// They agree in the ordinary case, because `set_location` fills in every entry
// that has not been told a place yet. They part company exactly when the
// athlete has already logged completed sets here and then names a different
// place — and there the reducer deliberately leaves those sets alone, because
// they really happened at the old one. So the list under the chip will NOT
// re-rank in that case. That is not a bug to patch here: it is the
// change-of-gym question, and `EQ-1-SWITCH` is the voice that asks it.

type Props = {
  visible: boolean;
  current: Exercise | null;
  catalog: Exercise[] | null;
  onClose: () => void;
  /** EQ-1: `instance` is present only when the athlete was actually asked
   *  which machine; omitted means nobody asked, and the caller resolves it. */
  onPick: (next: Exercise, instance?: string | null) => void;
  /** What the entry being swapped already carries, draft included — the
   *  machine minted this session is not in saved history yet. */
  currentInstance: string | null;
  /** EQ-1 slice D — where this session is happening. Ranks the machine trays
   *  and (later, DS-38) the list itself; it never hides anything. */
  location: string | null;
  /** The open session's entries — feeds the live load signature. */
  liveEntries: readonly DraftEntry[];
  /** Needed by the machine tray: the load signature is what tells two
   *  machines apart, so it has to render in the athlete's own unit. */
  unit: WeightUnit;
  /** EQ-1 slice D2b — the same place chip the logger's roster header and the
   *  add picker carry, as the two nodes `useGymChip` hands back. Antenore:
   *  *"nel add e nel replace, quello deve imperativamente utilizzare la stessa
   *  primitiva"* — so it arrives built, it is never re-solved here.
   *  ⚠️ It writes the SESSION's place, while `location` above ranks by THIS
   *  entry's. They agree until the two genuinely differ, which is the
   *  change-of-gym `EQ-1-SWITCH` has to ask about — see the header comment. */
  gymChip?: { trigger: ReactNode; tray: ReactNode };
  /** DS-42 — open straight onto the machine tray. `EQ-1-DISCOVER`'s popup
   *  ("want to set a variant?") lands the athlete ON that gesture instead of
   *  merely on the screen that holds it. */
  openOnMachineTray?: boolean;
  PainDot?: import('react').ComponentType<{ exercise: Exercise }>;
  usePainRiskLabel?: (exercise: Exercise) => string | null;
  /** EQ-1-GYM-COLDSTART — the athlete's declared absences, straight off the
   *  Profile the host already reads. Absent (with `patchProfile`) on a mount
   *  that has no profile door: the ✕ then simply does not appear. */
  locationUnavailable?: LocationUnavailable;
  /** EQ-1-PIN-DEFAULT — the athlete's elected machines, same Profile door.
   *  Absent = no ★, exactly as an absent `locationUnavailable` means no ✕. */
  equipmentDefault?: EquipmentDefault;
  /** The existing `deps.patchProfile` door, narrowed to the two fields the
   *  swap picker writes. They are two attributes on purpose (see
   *  equipmentDefault.ts): a demotion must never elect and an election must
   *  never sink a row. */
  patchProfile?: (patch: {
    locationUnavailable?: LocationUnavailable;
    equipmentDefault?: EquipmentDefault;
  }) => Promise<void>;
  notify?: (title: string, body: string) => void;
  /** PE-31 — can the HOST store the machine this picker would name? Absent =
   *  yes, which is the logger, so nothing about it changes. The program editor
   *  passes false: a `SlotDraft` has no `equipmentInstance` field, because a
   *  machine is a fact of the LOG (equipment-model.md §8.4) and not of a
   *  prescription. ⛔ Without this the band renders there too, and naming a
   *  machine in it resolves fine and stores nothing — the same silently-
   *  successful tap PE-26's review already killed once one layer down
   *  ([[feedback_a_noop_adapter_builds_a_fake_door]]). No door, no band. */
  canTagMachine?: boolean;
};

export function ExerciseSwapPicker({
  visible,
  canTagMachine = true,
  current,
  currentInstance,
  location,
  liveEntries,
  catalog,
  onClose,
  onPick,
  unit,
  gymChip,
  openOnMachineTray = false,
  PainDot,
  usePainRiskLabel,
  locationUnavailable,
  equipmentDefault,
  patchProfile,
  notify,
}: Props) {
  const theme = useTheme();
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  // EQ-1-GYM-COLDSTART — the declared-absence overlay for THIS place.
  // ⚠️ `location` is the ENTRY's place, the same one that ranks the machine
  // trays; the gym chip writes the SESSION's. They agree in the ordinary case
  // and part company only when the athlete moves mid-session, and there the
  // entry's is the more truthful key for "the machine in front of me".
  const unavailable = useLocationUnavailable({
    visible,
    // No door, no declaration: a ✕ that cannot write must not be shown.
    place: patchProfile ? location : null,
    declared: locationUnavailable,
    patchProfile: patchProfile ?? (() => Promise.resolve()),
    notify: notify ?? (() => undefined),
    t,
  });
  // EQ-1-PIN-DEFAULT — the ★ overlay for THIS place, keyed by the same
  // `location` the declaration above uses so an election and a demotion can
  // never be about two different rooms.
  const pin = useEquipmentDefault({
    visible,
    // No door, no election: a ★ that cannot write must not be shown.
    place: patchProfile ? location : null,
    elected: equipmentDefault,
    patchProfile: patchProfile ?? (() => Promise.resolve()),
    notify: notify ?? (() => undefined),
    t,
  });
  const [askUnavailable, setAskUnavailable] = useState(false);
  // ⚠️ Reset on close, not only on Cancel. The picker is mounted once and
  // toggled per entry, so leaving the ✕ dialog open and dismissing the whole
  // screen would bring it back on the NEXT entry the athlete opens — asking
  // about the gear of an exercise they never pressed the ✕ on. Same rule the
  // filters hook already follows for search and the chips.
  useEffect(() => {
    if (!visible) setAskUnavailable(false);
  }, [visible]);

  const filters = useSwapPickerFilters({
    visible,
    current,
    catalog,
    sunk: unavailable.sunk,
  });

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <SafeAreaView
        style={[styles.safe, { backgroundColor: theme.palette.bg }]}
        edges={['top', 'left', 'right', 'bottom']}
      >
        {/* The header holds the title and the way out, nothing else. Since
            DS-42 the place chip is no longer parked in its right cell: a
            session-wide gesture does not belong in the row that reads as
            chrome. */}
        <View style={styles.header}>
          <Pressable
            accessibilityRole="button"
            onPress={onClose}
            style={styles.cancelHit}
            hitSlop={{ top: 12, bottom: 12, left: 16, right: 8 }}
          >
            <Text style={styles.cancel}>{t('mobile.logger.cancel')}</Text>
          </Pressable>
          <Text style={styles.title} numberOfLines={1}>
            {t('swap.title')}
          </Text>
          <View style={styles.headerSpacer} />
        </View>

        {/* Band 1 — the session. */}
        <PickerSessionBand
          theme={theme}
          label={t('swap.bandSession', { defaultValue: 'Session' })}
          gymChip={gymChip}
        />

        {/* Band 2 — this exercise. ⛔ Only where a machine can be RECORDED. */}
        {current && canTagMachine ? (
          <CurrentExerciseMachineLine
            current={current}
            onPick={onPick}
            currentInstance={currentInstance}
            orderByLocation={location}
            liveEntries={liveEntries}
            unit={unit}
            initialExpanded={openOnMachineTray}
            onDeclareUnavailable={
              unavailable.place === null
                ? undefined
                : () => setAskUnavailable(true)
            }
            absentHere={unavailable.absentInstances(current.slug)}
            pin={
              pin.place === null
                ? undefined
                : {
                    pinned: pin.pinnedFor(current.slug),
                    onToggle: (value: string | null) =>
                      pin.toggle(current.slug, value),
                  }
            }
            theme={theme}
            styles={styles}
          />
        ) : null}

        {/* Band 3 — another exercise. */}
        <SwapPickerCandidateList
          filters={filters}
          catalog={catalog}
          onPick={onPick}
          orderByLocation={location}
          liveEntries={liveEntries}
          unit={unit}
          theme={theme}
          styles={styles}
          PainDot={PainDot}
          usePainRiskLabel={usePainRiskLabel}
          sunk={unavailable.sunk}
          onRestore={(ex) => unavailable.restore(ex.slug)}
          absentInstances={unavailable.absentInstances}
          pinnedFor={pin.place === null ? undefined : pin.pinnedFor}
        />

        {/* EQ-1-GYM-COLDSTART R2 — the question the ✕ raises. It renders INSIDE
            this screen's Modal (the same nesting the gym chip's naming dialog
            already uses from the band above), so it lands over the picker
            rather than behind it. */}
        {current !== null && unavailable.place !== null ? (
          <UnavailableHereDialog
            visible={askUnavailable}
            exercise={current}
            place={unavailable.place}
            // ⛔ The same survey `hasAvailableInstance` will be asked about, so
            // what the dialog offers and what decides the sink cannot disagree.
            survey={getInstanceSurveyForSlug(current.slug, unavailable.place)}
            declared={unavailable.declaredFor(current.slug)}
            onCancel={() => setAskUnavailable(false)}
            onConfirm={(answer) => {
              setAskUnavailable(false);
              unavailable.declare(current.slug, answer);
            }}
            theme={theme}
          />
        ) : null}
      </SafeAreaView>
    </Modal>
  );
}
